import React, { useState } from 'react';
import {
  X,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Clock
} from 'lucide-react';
import WorkflowTimeline from './WorkflowTimeline'; // ✅ Your new timeline view

interface TimelineEntry {
  id: string;
  name: string;
  type: 'start' | 'load' | 'agent' | 'condition' | 'branch' | 'api' | 'email' | 'end';
  status: 'Success' | 'Failed' | 'Running';
  time: string;
  input: string;
  output: string;
  nested?: TimelineEntry[];
}


interface LogRow {
  id: string;
  time: string;
  status: 'Running' | 'Completed' | 'Failed';
  detail: string;
}

interface Props {
  workflowId: string;
  name: string;
  onClose: () => void;
}

const mockLogs: LogRow[] = [
  {
    id: 'log1',
    time: '2025-07-09 10:15 AM',
    status: 'Running',
    detail: 'Workflow started execution on API trigger.',
  },
  {
    id: 'log2',
    time: '2025-07-08 05:40 PM',
    status: 'Completed',
    detail: 'Workflow completed successfully in 12.3s.',
  },
  {
    id: 'log3',
    time: '2025-07-08 04:30 PM',
    status: 'Failed',
    detail: 'Execution failed due to missing credentials.',
  },
];

const statusIcon = {
  Completed: CheckCircle,
  Failed: AlertTriangle,
  Running: Clock,
};

const statusColor = {
  Completed: 'text-green-400',
  Failed: 'text-red-400',
  Running: 'text-yellow-400',
};

const LogsModal: React.FC<Props> = ({ workflowId, name, onClose }) => {
  const [selectedLogEntry, setSelectedLogEntry] = useState<LogRow | null>(null);

  const getTimelineEntries = (logId: string): TimelineEntry[] => {
    // ⚠️ Replace with actual data fetched per `logId`
    return [
      {
        id: '1',
        name: 'Start',
        type: 'start',
        status: 'Success',
        time: '10:01 AM',
        input: '-',
        output: 'Workflow triggered',
      },
      {
        id: '2',
        name: 'Load Data',
        type: 'load',
        status: 'Success',
        time: '10:02 AM',
        input: 'Source info',
        output: '23 records',
      },
      {
        id: '3',
        name: 'Decision Agent',
        type: 'agent',
        status: 'Success',
        time: '10:03 AM',
        input: 'Data chunk',
        output: 'Flagged 5 risky',
        nested: [
          {
            id: '3.1',
            name: 'Sub Agent A',
            type: 'agent',
            status: 'Success',
            time: '10:03:20',
            input: 'Row 1',
            output: 'Score: 0.92',
          },
        ],
      },
      {
        id: '4',
        name: 'Condition Checker',
        type: 'condition',
        status: 'Success',
        time: '10:04 AM',
        input: 'Flagged rows',
        output: 'True',
      },
      {
        id: '5',
        name: 'Send Email',
        type: 'email',
        status: 'Success',
        time: '10:05 AM',
        input: 'Alert Template',
        output: 'Email sent',
      },
      {
        id: '6',
        name: 'End',
        type: 'end',
        status: 'Success',
        time: '10:06 AM',
        input: '-',
        output: 'Workflow completed',
      },
    ];
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-start pt-20 px-4">
      <div className="bg-neutral-900 w-full max-w-3xl rounded-xl shadow-lg border border-neutral-800 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <h2 className="text-white text-lg font-semibold">
            {selectedLogEntry
              ? `Workflow Timeline – ${name}`
              : `Logs for Workflow – ${name}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 max-h-[65vh] overflow-y-auto text-sm text-white">
          {selectedLogEntry ? (
            <>
              <button
                onClick={() => setSelectedLogEntry(null)}
                className="mb-4 text-xs text-gray-400 hover:text-white flex items-center gap-1"
              >
                <ChevronRight className="rotate-180 w-4 h-4" />
                Back to log list
              </button>
              <WorkflowTimeline entries={getTimelineEntries(selectedLogEntry.id)} />
            </>
          ) : (
            mockLogs.map((log) => {
              const Icon = statusIcon[log.status];
              return (
                <div
                  key={log.id}
                  className="border border-neutral-700 bg-neutral-800 hover:bg-neutral-700/60 transition rounded p-4 mb-3"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${statusColor[log.status]}`} />
                        <span className={`font-semibold ${statusColor[log.status]}`}>
                          {log.status}
                        </span>
                      </div>
                      <div className="text-gray-400 text-xs mt-1">{log.time}</div>
                      <div className="text-sm mt-2">{log.detail}</div>
                    </div>
                    <div>
                      <button
                        onClick={() => setSelectedLogEntry(log)}
                        className="text-indigo-400 text-xs hover:underline"
                      >
                        View Timeline
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-800 text-right bg-neutral-950">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogsModal;
